import type { ReactNode } from 'react';
import { css } from 'styled-system/css';
import { Container } from './Container';
import { MarketingPage } from './MarketingPage';

export function LegalProse({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <MarketingPage>
      <section
        aria-label={title}
        className={css({
          paddingTop: { base: '28', md: '36' },
          paddingBottom: { base: '16', md: '24' },
        })}
      >
        <Container>
          <article
            className={css({
              maxWidth: '[72ch]',
              display: 'flex',
              flexDirection: 'column',
              gap: '8',
              '& header': {
                display: 'flex',
                flexDirection: 'column',
                gap: '3',
              },
              '& h1': {
                fontSize: { base: '3xl', md: '4xl' },
                letterSpacing: 'tight',
                margin: '0',
                textWrap: 'balance',
              },
              '& h2': {
                fontSize: 'xl',
                fontWeight: 'semibold',
                letterSpacing: 'tight',
                margin: '0',
                color: 'text.primary',
                textWrap: 'balance',
              },
              '& section': {
                display: 'flex',
                flexDirection: 'column',
                gap: '4',
              },
              '& p, & li': {
                margin: '0',
                color: 'text.secondary',
                lineHeight: 'relaxed',
                textWrap: 'pretty',
              },
              '& ul': {
                margin: '0',
                paddingInlineStart: '5',
                display: 'flex',
                flexDirection: 'column',
                gap: '3',
              },
              '& strong': {
                color: 'text.primary',
                fontWeight: 'semibold',
              },
              '& code': {
                fontFamily: 'mono',
                fontSize: 'sm',
                color: 'text.primary',
              },
              '& a': {
                color: 'text.primary',
                textDecoration: 'underline',
                textDecorationColor: 'border.hairline',
                textUnderlineOffset: '4px',
                _hover: { textDecorationColor: 'text.primary' },
                _focusVisible: {
                  outline: '2px solid token(colors.border.focus)',
                  outlineOffset: '2px',
                },
              },
              '& [data-legal-table]': {
                overflowX: 'auto',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 'sm',
                lineHeight: 'relaxed',
              },
              '& th, & td': {
                border: '1px solid token(colors.border.hairline)',
                padding: '2',
                textAlign: 'left',
                verticalAlign: 'top',
              },
              '& th': {
                color: 'text.primary',
                fontWeight: 'semibold',
              },
              '& td': {
                color: 'text.secondary',
              },
            })}
          >
            {children}
          </article>
        </Container>
      </section>
    </MarketingPage>
  );
}
